import { useQuery } from '@tanstack/react-query'
import { UUID } from 'crypto'
import { getUserOnboardingData } from '@/api/usersAPI'
import { OnboardingDataResponse } from '@/models/Users'

interface UseOnboardingDataOptions {
  user_uuid: UUID | null | undefined
  token: string | null
  includeDebug?: boolean
  enabled?: boolean
  selected_org_uuid?: UUID
}

/**
 * Custom hook to fetch onboarding data from the new data-only endpoint.
 * This hook provides raw data without step logic, allowing the frontend
 * to own all step management and UI logic.
 */
export const useOnboardingData = ({
  user_uuid,
  token,
  includeDebug = false,
  enabled = true,
  selected_org_uuid,
}: UseOnboardingDataOptions) => {
  return useQuery<OnboardingDataResponse>({
    queryKey: ['onboardingData', user_uuid, includeDebug, selected_org_uuid],
    queryFn: async () => {
      if (!user_uuid || !token) {
        throw new Error('User UUID and token are required')
      }

      return getUserOnboardingData({
        user_uuid,
        token,
        includeDebug,
        selected_org_uuid,
      })
    },
    enabled: enabled && !!user_uuid && !!token,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (was cacheTime in v4)
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    retry: (failureCount, error) => {
      // Don't retry on 4xx errors
      if (error instanceof Error && error.message.includes('4')) {
        return false
      }
      return failureCount < 3
    },
  })
}

